/*
 * If not stated otherwise in this file or this component's LICENSE file the
 * following copyright and licenses apply:
 *
 * Copyright 2022 Metrological
 *
 * Licensed under the Apache License, Version 2.0 (the License);
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Colors, Img, Language, Lightning, Profile, Registry, Router, Storage, Utils } from "@lightningjs/sdk";
import routes from "./routes";
import { Menu } from "./widgets/Menu/Menu";
import { getHomePage, isExternalPlatform, isPostTvPlatform, STORAGE_KEYS } from "./lib/utils";
import { Loading } from "./components/feedback/Loading/Loading";
import { PageBackground } from "./components/layout/PageBackground/PageBackground";
import { Auth } from "./widgets/Auth/Auth";
import { Subscription } from "./widgets/Subscription/Subscription";
import { Exit } from "./widgets/Exit/Exit";
import { IAPSubscription } from "./widgets/IAPSubscription/IAPSubscription";
import theme from "./lib/theme";
import { PostTvSubscription } from "./widgets/PostTvSubscription/PostTvSubscription";
import { PinInput } from "./widgets/PinInput/PinInput";
import { destroyPlayer } from "./lib/video-player";
import { Lifecycle, Metrics } from "@firebolt-js/sdk";
import { postTvWebSocket } from "./lib/websocket";
import { isPostTvLinux } from "./lib/utils";
import ThemeCustomizer from "./components/configurations/Theme/ThemeCustomizer";
import { Theme, themeManager } from "./components/configurations/Theme/ThemeManager";
import ColorTrayIcon from "./components/configurations/Theme/ColorTrayIcon";
import configs from "./lib/configs";
import { AppEvents } from "./types/app-events";

const appBuildNumber = process.env.APP_BUILD_NUMBER || `1`;

interface AppTemplateSpec extends Lightning.Component.TemplateSpecLoose {
    Background: typeof PageBackground;
    Pages: object;
    Loading: {
        Label: object;
        Loader: typeof Loading;
    };
    Widgets: {
        Menu: typeof Menu;
        Auth: typeof Auth;
        Subscription: typeof Subscription;
        Exit: typeof Exit;
        IAPSubscription: typeof IAPSubscription;
        PostTvSubscription: typeof PostTvSubscription;
        PinInput: typeof PinInput;
        ThemeCustomizer: typeof ThemeCustomizer;
    };
    ColorTrayIcon: typeof ColorTrayIcon;
}

export class App
    extends Router.App<AppTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<AppTemplateSpec>
{
    /*
     * The following properties exist to make it more convenient to access elements
     * below in a type-safe way. They are optional.
     *
     * See https://lightningjs.io/docs/#/lightning-core-reference/TypeScript/Components/TemplateSpecs?id=using-a-template-spec
     * for more information.
     */

    _zeasnKeys: any = {};
    _zeasnKeyTriggered = false;
    _lastKeyPressedAt = 0;
    _lastKnownHash: string = "";
    // Current top-level page (from the setActivePage event). Reliable for the
    // back handler, unlike Router.getActiveHash() with backtrack + clearHistory.
    _currentPage: string = "";
    _hashCheckInterval: NodeJS.Timeout | null = null;

    _boundEventHandlers: any = {};

    readonly Exit = this.getByRef("Widgets")!.getByRef("Exit")!;
    readonly PinInput = this.getByRef("Widgets")!.getByRef("PinInput")!;
    private _themeCallback!: (theme: Theme | undefined) => void;
    private _boundLifecycleHandler?: (state: string) => void;

    override _setup() {
        // @ts-ignore
        Router.startRouter(routes);

        try {
            Metrics.appInfo(appBuildNumber);
        } catch (error) {
            console.error("Error calling Metrics.appInfo:", error);
        }

        this._boundLifecycleHandler = (state) => this._onLifecycleStateChange(state);
        Lifecycle.listen(this._boundLifecycleHandler);
        Lifecycle.ready();

        const languages = Language.available();
        const preselectedLang = Storage.get(STORAGE_KEYS.selectedLanguage) || "en";
        if (!languages.find((lang: { code: string }) => lang.code === preselectedLang)) {
            Storage.set(STORAGE_KEYS.selectedLanguage, languages[0]?.code || "en");
        }

        // Show ThemeCustomizer only if demo is true in configs
        const themeCustomizer = this.getByRef("Widgets")?.getByRef("ThemeCustomizer");
        if (themeCustomizer && configs.demo === true) {
            themeCustomizer.patch({
                visible: true,
                alpha: 1
            });
        }

        // Initialize the last known hash
        this._lastKnownHash = window.location.hash.replace("#", "");

        this._boundEventHandlers = {
            _handleAppClose: this._handleAppClose.bind(this),
            _exitApp: this._exitApp.bind(this),
            _handleOffline: this._handleOffline.bind(this),
            _handleOnline: this._handleOnline.bind(this),
            _handlePopState: this._handlePopState.bind(this),
            _handleHashChange: this._handleHashChange.bind(this)
        };

        Registry.addEventListener(window, "offline", this._boundEventHandlers._handleOffline);
        Registry.addEventListener(window, "online", this._boundEventHandlers._handleOnline);
        Registry.addEventListener(window, "popstate", this._boundEventHandlers._handlePopState);
        Registry.addEventListener(window, "hashchange", this._boundEventHandlers._handleHashChange);

        // Track the active top-level page so Back knows whether we're on home.
        this.application.on("setActivePage", (name: string) => {
            if (name) this._currentPage = name;
        });

        // Set up periodic hash checking to ensure we track all navigation
        this._hashCheckInterval = setInterval(() => {
            const currentHash = window.location.hash.replace("#", "");
            if (currentHash !== this._lastKnownHash) {
                this._lastKnownHash = currentHash;
            }
        }, 100);

        // Also update hash tracking after a short delay to ensure router has initialized
        setTimeout(() => {
            this._updateHashTracking();
        }, 500);

        // const query = Router.getQueryStringParams()
        // if(query && query.debug){
        //     Registry.addEventListener(window, "debug", this._debug.bind(this));
        //
        //     window.dispatchEvent(
        //         new CustomEvent("debug", {
        //             detail: {
        //                 name: "appStart",
        //                 data: { test: "test" }
        //             }
        //         })
        //     );
        //     this.tag("Debug").patch({
        //         alpha: 1
        //     })
        // }
    }

    private _onLifecycleStateChange(state: string) {
        // Map Comcast states → your app events
        // See: Foreground / Background / Suspended / Exit, etc.
        switch (state) {
            case "background":
                this.application.emit(AppEvents.PlayerPauseAndQuiesce); // pause & stop pings
                break;
            case "suspended":
                this.application.emit(AppEvents.PlayerPauseAndQuiesce); // same as bg
                break;
            case "inactive":
                this.application.emit(AppEvents.PlayerStop); // hard stop to close all A/V resources
                break;
            case "foreground":
                this.application.emit(AppEvents.PlayerMaybeResumeOrRestore); // resume or restore if on player
                break;
            case "exit":
                this.application.emit(AppEvents.PlayerStop); // hard stop
                break;
            default:
                // handle other states if Comcast SDK defines them (initializing, etc.)
                break;
        }
    }

    static override _template(): Lightning.Component.Template<AppTemplateSpec> {
        return {
            w: theme.layout.w,
            h: theme.layout.h,
            collision: true,
            Background: { type: PageBackground },
            Pages: { forceZIndexContext: true, collision: true },
            Loading: {
                forceZIndexContext: true,
                visible: false,
                zIndex: 122,
                Loader: { zIndex: 122, type: Loading, isMain: true }
            },
            Debug: {
                w: 1920,
                h: 100,
                rect: true,
                color: Colors("#000000").get(),
                clipping: true,
                zIndex: 999,
                y: 1080 - 100,
                x: 0,
                flex: { direction: "column", gap: 10 },
                alpha: 0
            },
            Widgets: {
                Menu: {
                    type: Menu
                },
                Auth: {
                    type: Auth
                },
                Subscription: {
                    type: Subscription
                },
                PostTvSubscription: {
                    type: PostTvSubscription
                },
                PinInput: {
                    type: PinInput
                },
                Exit: {
                    type: Exit
                },
                IAPSubscription: {
                    type: IAPSubscription
                },
                ThemeCustomizer: {
                    type: ThemeCustomizer,
                    alpha: 0,
                    visible: false,
                    zIndex: 999
                }
            },
            ColorTrayIcon: {
                type: ColorTrayIcon
            }
        };
    }

    static language() {
        return {
            file: Utils.asset("lang.json"),
            language: this._getLanguage()
        };
    }

    static getFonts() {
        return [
            {
                family: "Regular",
                url: Utils.asset("fonts/Regular.ttf") as string
            },
            {
                family: "Medium",
                url: Utils.asset("fonts/Medium.ttf") as string
            },
            {
                family: "SemiBold",
                url: Utils.asset("fonts/SemiBold.ttf") as string
            },
            {
                family: "Bold",
                url: Utils.asset("fonts/Bold.ttf") as string
            },
            {
                family: "Heavy",
                url: Utils.asset("fonts/ExtraBold.ttf") as string
            },
            {
                family: "Secondary-Regular",
                url: Utils.asset("fonts/Secondary-Regular.ttf") as string
            },
            {
                family: "Secondary-Medium",
                url: Utils.asset("fonts/Secondary-Regular.ttf") as string
            },
            {
                family: "Secondary-SemiBold",
                url: Utils.asset("fonts/Secondary-SemiBold.ttf") as string
            }
        ];
    }

    _debug({ detail: { name, data } }: any) {
        console.log(`[${Date.now()}] ${JSON.stringify({ name, data })})`);

        this.tag("Debug").children = [
            {
                text: {
                    text: `[${Date.now()}] ${JSON.stringify({ name, data })})`,
                    fontSize: 16,
                    wordWrapWidth: 400,
                    wordWrap: true,
                    textColor: Colors("#ffffff").get()
                }
            },
            ...this.tag("Debug").children
        ];
    }

    override _init() {
        if (typeof window.VK_PLAY !== "undefined") {
            this._zeasnKeys = {
                VK_PLAY: {
                    key: window.VK_PLAY,
                    value: 415
                },
                VK_STOP: {
                    key: window.VK_STOP,
                    value: 413
                },
                VK_PAUSE: {
                    key: window.VK_PAUSE,
                    value: 19
                },
                VK_PLAY_PAUSE: {
                    key: window.VK_PLAY_PAUSE,
                    value: 10252
                },
                VK_BACK: {
                    key: window.VK_BACK,
                    value: 461
                },
                VK_BACK_SPACE: {
                    key: window.VK_BACK_SPACE,
                    value: 461
                },
                VK_FAST_FWD: {
                    key: window.VK_FAST_FWD,
                    value: 417
                },
                VK_REWIND: {
                    key: window.VK_REWIND,
                    value: 412
                },
                VK_LEFT: { key: window.VK_LEFT, value: 37 },
                VK_RIGHT: { key: window.VK_RIGHT, value: 39 }
            };
        }

        if (isExternalPlatform()) {
            Registry.addEventListener(window, "handleAppExit", this._boundEventHandlers._exitApp);
        }
        this.application.on("handleAppExit", this._boundEventHandlers._exitApp);

        this._themeCallback = this._onThemeChange.bind(this);
        themeManager.subscribe(this._themeCallback);
    }

    private _onThemeChange(customTheme: Theme | undefined): void {
        const logo = this.tag("Menu")?.tag("Logo");
        if (logo && customTheme?.logoUrl) {
            logo.patch({
                texture: Img(customTheme.logoUrl).contain(theme.logo.w, theme.logo.h)
            });
        } else if (logo && !customTheme?.logoUrl) {
            // Reset to default logo if no custom logo URL
            logo.patch({
                texture: Img(Utils.asset("images/logo.png")).contain(theme.logo.w, theme.logo.h)
            });
        }
    }

    override _detach(): void {
        themeManager.unsubscribe(this._themeCallback);

        // Remove event listeners
        if (this._boundEventHandlers._handlePopState) {
            window.removeEventListener("popstate", this._boundEventHandlers._handlePopState, true);
        }
        if (this._boundEventHandlers._handleHashChange) {
            Registry.removeEventListener(window, "hashchange", this._boundEventHandlers._handleHashChange);
        }

        // Clear the hash check interval
        if (this._hashCheckInterval) {
            clearInterval(this._hashCheckInterval);
            this._hashCheckInterval = null;
        }
    }

    override _captureKey(e: KeyboardEvent) {
        // Handle Android back button (keyCode 8 Backspace or 461 VK_BACK)
        const keyCode = e.keyCode || (e as any).code || (e as any).keyIdentifier;
        // const isAndroidBack = keyCode === 8 || keyCode === 461 || e.key === "Backspace";
        //
        // if (isAndroidBack) {
        //     // Check if any widget (except Menu), ThemeCustomizer, or filters are open
        //     const activeWidget = Router.getActiveWidget();
        //     const isWidgetOpen =
        //         activeWidget && activeWidget.ref !== "Menu" && activeWidget.ref !== "PostTvSubscription";
        //     const isThemeCustomizerOpen = ThemeCustomizer.isOpen;
        //     const isFiltersOpen = this._areFiltersOpen();
        //
        //     if (isWidgetOpen || isThemeCustomizerOpen || isFiltersOpen) {
        //         // Prevent browser history navigation when widgets/filters are open
        //         e.preventDefault();
        //         e.stopPropagation();
        //         // Handle back button to close widgets/filters instead
        //         // @ts-ignore
        //         // this._handleBack(e);
        //         Router.focusPage();
        //         return true;
        //     }
        // }

        e.preventDefault();

        // for LG !!
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // @ts-ignore
        // console.log("keyCode", keyCode, e.keyCode, e.code, e.keyIdentifier);

        // Check for 't' key press (keyCode 84 or key 't') to open side panel only if demo is true
        if (((e.shiftKey && keyCode === 84) || (e.shiftKey && e.key === "t")) && configs.demo === true) {
            console.log("T key detected, opening side panel (demo mode)");
            this._toggleSidePanel();
            return true;
        }

        if (!e.keyCode && e.keyCode !== 0 && keyCode) {
            window.dispatchEvent(new KeyboardEvent("keydown", { ...e, keyCode: keyCode }));
            return true;
        }

        const zeasnKey = Object.keys(this._zeasnKeys).find((key) => this._zeasnKeys[key].key === keyCode);
        // console.log("is titanos key:", zeasnKey);
        if (zeasnKey && !this._zeasnKeyTriggered) {
            this._zeasnKeyTriggered = true;
            window.dispatchEvent(new KeyboardEvent("keydown", { ...e, keyCode: this._zeasnKeys[zeasnKey].value }));
            return true;
        }
        this._zeasnKeyTriggered = false;

        const activeHash = Router.getActiveHash();

        if (activeHash === "playback") return false;

        // const delay = e.repeat ? 280 : 150;
        const delay = 150;
        const now = Date.now();

        if (now - this._lastKeyPressedAt < delay) {
            return true;
        }
        this._lastKeyPressedAt = now;

        return Router.isNavigating();
    }

    // Helper method to check if filters are currently open
    _areFiltersOpen(): boolean {
        try {
            const activePage = Router.getActivePage();
            if (!activePage) return false;

            // FIRST: Check page state - if it's in "FiltersState", filters are open
            // This is the most reliable indicator
            const pageState = (activePage as any)._state;
            if (pageState === "FiltersState" || pageState === "FilterState") {
                return true;
            }

            // Check if the active page has a Filters component
            const filtersComponent = activePage.getByRef?.("Filters");
            if (filtersComponent) {
                // Check if Button is active (indicates filters are open)
                const button = filtersComponent.getByRef?.("Button");
                if (button && (button as any).isActive === true) {
                    return true;
                }

                // Check if Background has scaleY > 0 (filters are open)
                const background = filtersComponent.getByRef?.("Background");
                if (background && background.scaleY > 0) {
                    return true;
                }

                // Also check Filters.scaleY
                if (filtersComponent.scaleY > 0) {
                    return true;
                }
            }

            return false;
        } catch (e) {
            console.log("Error checking filters state:", e);
            return false;
        }
    }

    override _handleLeft() {
        Router.focusWidget("Menu");
    }

    _toggleSidePanel() {
        // Only toggle if demo mode is enabled
        if (configs.demo !== true) {
            console.log("ThemeCustomizer disabled: demo mode is not enabled");
            return Promise.resolve(false);
        }
        const sidePanel = this.getByRef("Widgets")?.getByRef("ThemeCustomizer");

        return new Promise((resolve): void => {
            try {
                if (sidePanel) {
                    // Check if the panel is already open
                    if (ThemeCustomizer.isOpen) {
                        // Close the panel if it's open
                        sidePanel.close();
                        // Return focus to the previous element
                        Router.focusPage();
                        resolve(false);
                    } else {
                        // Open the panel if it's closed
                        sidePanel.open(resolve);
                        Router.focusWidget("ThemeCustomizer");
                    }
                } else {
                    console.error("SidePanel component not found");
                    resolve(false);
                }
            } catch (error) {
                console.error("Error toggling SidePanel:", error);
                resolve(false);
            }
        });
    }

    private static _getLanguage() {
        return new Promise((resolve) => {
            (async () => {
                const savedLang = Storage.get(STORAGE_KEYS.selectedLanguage);

                if (savedLang) {
                    resolve(savedLang);
                    return;
                }

                const query = Router.getQueryStringParams();
                const queryLang = query ? query.lang : undefined;

                const profileLang = await Profile.language();

                const lang = queryLang || profileLang || "en";

                Storage.set(STORAGE_KEYS.selectedLanguage, lang);
                resolve(lang);
            })();
        });
    }

    override _handleBack() {
        this._handleAppClose();
    }

    // Router calls this on Back at the root — and with `backtrack` + `clearHistory`
    // every top-level page (movies, live tv, series…) is "root". From a non-home
    // page, go home instead of prompting to exit; only on home show the dialog.
    override _handleAppClose() {
        const homePage = getHomePage();
        if (this._currentPage && this._currentPage !== homePage) {
            this._currentPage = homePage;
            Router.navigate(homePage);
            return;
        }
        this._exitApp();
    }

    // Actually exit the app (confirm dialog + platform close). Used by Back on
    // home and by the explicit Exit menu item.
    _exitApp() {
        this._toggleExitDialog().then((confirmed) => {
            // close the application
            if (confirmed) {
                try {
                    destroyPlayer();
                } catch (e) {
                    console.log("Error on player destroy", e);
                }

                try {
                    Lifecycle.close(Lifecycle.CloseReason.REMOTE_BUTTON)
                        .then((success) => {
                            console.log(success);
                            // @ts-ignore
                            this.application.closeApp();
                        })
                        .catch((error) => console.log(error));
                } catch (e) {
                    console.log(e);
                }

                // Android-specific exit strategies
                if (this._isAndroidEnvironment()) {
                    this._handleAndroidExit();
                    return;
                }

                try {
                    // Close WebSocket connection on app exit (only if useParentalPin is enabled)
                    const useParentalPin = configs.useParentalPin === true;
                    if (isPostTvLinux() && useParentalPin) {
                        try {
                            postTvWebSocket.close();
                            console.log("[App] WebSocket closed on exit");
                        } catch (e) {
                            console.error("[App] Error closing WebSocket:", e);
                        }
                    }

                    if (typeof window.SmartTvA_API !== "undefined") {
                        window.SmartTvA_API.exit();
                    }

                    if (isPostTvPlatform()) {
                        window.close();
                    } else {
                        window.history.go(-999);

                        window.close();
                    }
                } catch (e) {
                    console.log("e", e);
                }
            }
        });
    }

    _toggleExitDialog() {
        return new Promise((resolve): void => {
            this.Exit.open(resolve);
            Router.focusWidget("Exit");
        });
    }

    _handlePopState(event: PopStateEvent) {
        // Use native browser history API for more reliable popstate handling
        const currentHash = window.location.hash.replace("#", "");

        // Handle filters state changes from history navigation
        // When popstate fires, event.state is the state we're navigating TO
        const targetState = event.state || {};

        // Check URL search params as fallback if state doesn't have filters property
        const urlParams = new URLSearchParams(window.location.search);
        const urlHasFilters = urlParams.get("filters") === "true";

        // Determine if filters should be open based on state or URL
        const filtersShouldBeOpen =
            targetState.filters === true || (targetState.filters === undefined && urlHasFilters);
        const filtersAreCurrentlyOpen = this._areFiltersOpen();

        // If state is null, we're navigating back to a state that existed before we started tracking
        // In this case, if URL doesn't have filters=true, we should close filters
        // Always close filters when state is null and URL doesn't have filters (don't rely on detection)
        if (event.state === null && !urlHasFilters) {
            this._closeFiltersFromHistory();
        }
        // If state is null but URL has filters, filters should be open
        else if (event.state === null && urlHasFilters && !filtersAreCurrentlyOpen) {
            this._openFiltersFromHistory();
        }
        // Sync UI with history state
        // If state has filters property, use it; otherwise check URL params
        else if (targetState.hasOwnProperty("filters") || urlHasFilters) {
            if (filtersShouldBeOpen && !filtersAreCurrentlyOpen) {
                // History says filters should be open, but they're closed - open them
                this._openFiltersFromHistory();
            } else if (!filtersShouldBeOpen && filtersAreCurrentlyOpen) {
                // History says filters should be closed, but they're open - close them
                this._closeFiltersFromHistory();
            }
        }

        // If we don't have a previous hash stored, try to get it from the browser history
        let previousHash = this._lastKnownHash;
        if (!previousHash && window.history.state && (window.history.state as any).previousHash) {
            previousHash = (window.history.state as any).previousHash;
        }

        // Only exit when backing out of the HOME page to the root. From any other
        // page (movies, live tv, series…) the root redirect takes us home instead.
        // Uses the tracked current page (previousHash is unreliable with
        // backtrack + clearHistory and wrongly stayed "vod" for sub-pages).
        if (currentHash == "$" && this._currentPage === getHomePage()) {
            // Call only the selected code instead of the entire _handleAppClose function
            try {
                destroyPlayer();
            } catch (e) {
                console.log("Error on player destroy", e);
            }

            try {
                Lifecycle.close(Lifecycle.CloseReason.REMOTE_BUTTON)
                    .then((success) => {
                        console.log(success);
                        // @ts-ignore
                        this.application.closeApp();
                    })
                    .catch((error) => console.log(error));
            } catch (e) {
                console.log(e);
            }
            console.log("Closing app due to popstate xxxxxx");

            // Android-specific exit strategies
            if (this._isAndroidEnvironment()) {
                this._handleAndroidExit();
                return;
            }

            try {
                if (typeof window.SmartTvA_API !== "undefined") {
                    window.SmartTvA_API.exit();
                }

                if (isPostTvPlatform()) {
                    window.close();
                } else {
                    window.history.go(-999);

                    window.close();
                }
            } catch (e) {
                console.log("e", e);
            }
        }

        // Update the last known hash for next popstate event
        this._lastKnownHash = currentHash;
    }

    _closeFiltersFromHistory() {
        try {
            const activePage = Router.getActivePage();
            if (!activePage) {
                return;
            }

            // Check if the active page is MediaCategoryName
            const mediaCategory = activePage as any;
            if (!mediaCategory) {
                return;
            }

            if (typeof mediaCategory._toggleFilters !== "function") {
                return;
            }

            // Close filters without navigating (since we're already handling history)
            // Pass skipHistory=true to prevent creating a new history entry
            // _toggleFilters now handles both List position and filters component state
            mediaCategory._toggleFilters(false, true);
            console.log("[_closeFiltersFromHistory] Completed");
        } catch (e) {
            console.log("[_closeFiltersFromHistory] Error:", e);
        }
    }

    _openFiltersFromHistory() {
        try {
            const activePage = Router.getActivePage();
            if (!activePage || Router.isNavigating()) {
                return;
            }

            // Check if the active page is MediaCategoryName
            const mediaCategory = activePage as any;
            if (!mediaCategory || typeof mediaCategory._toggleFilters !== "function") {
                return;
            }

            // Open filters without navigating (since we're already handling history)
            // Pass skipHistory=true to prevent creating a new history entry
            // _toggleFilters now handles both List position and filters component state
            mediaCategory._toggleFilters(true, true);
        } catch (e) {
            console.log("Error opening filters from history:", e);
        }
    }

    _handleHashChange() {
        // Update the last known hash when hash changes (programmatic navigation)
        const newHash = window.location.hash.replace("#", "");
        this._lastKnownHash = newHash;
    }

    _updateHashTracking() {
        // Manually update hash tracking - can be called from other parts of the app
        const currentHash = window.location.hash.replace("#", "");
        if (currentHash !== this._lastKnownHash) {
            this._lastKnownHash = currentHash;
        }
    }

    /**
     * Detects if the app is running in an Android environment
     * @returns true if running on Android, false otherwise
     */
    private _isAndroidEnvironment(): boolean {
        // Check URL parameters for Android flag
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("android") === "true") {
            return true;
        }

        // Check user agent for Android
        if (navigator.userAgent && navigator.userAgent.indexOf("Android") !== -1) {
            return true;
        }

        // Check for Android WebView interface
        if (typeof window.Android !== "undefined" || window.Android) {
            return true;
        }

        // Check for Cordova/PhoneGap environment
        if (typeof (window as any).cordova !== "undefined" || typeof (window as any).phonegap !== "undefined") {
            return true;
        }

        return false;
    }

    /**
     * Handles Android app exit with multiple fallback strategies
     * Prioritizes JavaScript interface methods over URL schemes to avoid ERR_UNKNOWN_URL_SCHEME errors
     */
    private _handleAndroidExit(): void {
        // First try the Android WebView JavaScript interface
        try {
            if (typeof window.Android !== "undefined" && window.Android && window.Android.exitApp) {
                console.log("[Attempting Android.exitApp]");
                window.Android.exitApp();
                return;
            }
        } catch (err) {
            console.log("[Android.exitApp failed]", err);
        }

        // Try Cordova/PhoneGap exit method
        try {
            if (typeof (window as any).cordova !== "undefined" && (window as any).cordova.exec) {
                console.log("[Attempting cordova.exec exit]");
                (window as any).cordova.exec(null, null, "ExitApp", "exitApp", []);
                return;
            }
        } catch (err) {
            console.log("[cordova.exec exit failed]", err);
        }

        // Try the custom URL scheme as last resort (may cause ERR_UNKNOWN_URL_SCHEME in some environments)
        try {
            console.log("[Attempting app://close as fallback]");
            location.href = "app://close";
            return;
        } catch (err) {
            console.log("[app://close failed]", err);
        }

        // Fallback methods
        try {
            if (window.history && window.history.length > 1) {
                console.log("[Attempting history.back]");
                window.history.back();
                return;
            }
        } catch (err) {
            console.log("[history.back failed]", err);
        }

        // Final fallback
        console.log("[Attempting window.close as final fallback]");
        window.close();
    }

    _handleOffline() {
        Metrics.error(Metrics.ErrorType.NETWORK, "404", "Something went wrong", true);
    }

    _handleOnline() {}
}
